/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row, Card, Modal, Form } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import {
  getAllAgentsByEnvironment,
  deleteAgent,
  changeAgentEnvironment,
  updateAgent,
  createAgent,
  checkCampaignAgent
} from 'services/hvaagent.service';
import { fetchHVABXPCampaigns } from 'services/hvaservicecampaign.service';
import { hvaImagesandVoices } from 'pages/hva/hvaImagesandVoices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPersonDotsFromLine,
  faCode,
  faDigging,
  faChargingStation,
  faComments,
  faPlay,
  faEdit,
  faTrash,
  faExchange,
  faPlus,
  faChevronUp,
  faChevronDown,
  faInfoCircle,
  faCircleNotch,
  faSave,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import Avatar from 'components/base/Avatar';
import HVAToast from 'components/common/HVAToast';

// Importaciones de banderas -- derecha
import spanish from 'assets/img/flags/spain.png';
import english from 'assets/img/flags/england.png';
import american from 'assets/img/flags/usa.png';
import greek from 'assets/img/flags/greece.png';
import mexican from 'assets/img/flags/mexico.png';
import german from 'assets/img/flags/germany.png';
import brasilian from 'assets/img/flags/brazil.png';
import italian from 'assets/img/flags/italy.png';
import chilean from 'assets/img/flags/chile.png';
import turkish from 'assets/img/flags/turkey.png';
import colombian from 'assets/img/flags/colombia.png';
import peruvian from 'assets/img/flags/peru.png';
import argentine from 'assets/img/flags/argentina.png';
import french from 'assets/img/flags/france.png';

// Banderas -- izquierda
import spanishl from 'assets/img/flags/spainl.png';
import englishl from 'assets/img/flags/englandl.png';
import americanl from 'assets/img/flags/usal.png';
import greekl from 'assets/img/flags/greecel.png';
import germanl from 'assets/img/flags/germanyl.png';
import italianl from 'assets/img/flags/italyl.png';
import frenchl from 'assets/img/flags/francel.png';
import brasilianl from 'assets/img/flags/brazill.png';
import chileanl from 'assets/img/flags/chilel.png';
import mexicanl from 'assets/img/flags/mexicol.png';

type Language =
  // Español
  | 'spanish'
  | 'chilean'
  | 'mexican'
  | 'argentine'
  | 'colombian'
  | 'peruvian'
  | 'venezuelan'
  | 'ecuadorian'
  | 'uruguayan'
  | 'bolivian'
  | 'paraguayan'
  | 'costarican'
  | 'panamanian'
  | 'dominican'
  | 'guatemalan'
  | 'honduran'
  | 'salvadoran'
  | 'nicaraguan'
  | 'cuban'
  // Inglés
  | 'english'
  | 'american'
  // Portugués
  | 'brasilian'
  | 'portuguese'
  // Europeos
  | 'french'
  | 'german'
  | 'italian'
  | 'greek'
  | 'swedish'
  | 'norwegian'
  | 'polish'
  | 'dutch'
  | 'danish'
  | 'finnish'
  | 'czech'
  | 'romanian'
  | 'hungarian'
  | 'turkish';

interface Agent {
  agentid: string;
  agentname: string;
  testtitle: string;
  description: string;
  environment: string;
  active: boolean;
  clientowner: string;
  language: string;
  flag: string;
  bxpname: string;
  campaign?: {
    id: number;
    name: string;
    bxpcampaignid: number;
    active: boolean;
  };
}

interface GroupedAgents {
  Development: Agent[];
  POC: Agent[];
  Staging: Agent[];
  Production: Agent[];
}

const LANGUAGE_FLAGS: Record<string, string> = {
  'es-ES': spanishl,
  'es-CL': chileanl,
  'es-MX': mexicanl,
  'en-GB': englishl,
  'en-US': americanl,
  'el-GR': greekl,
  'fr-FR': frenchl,
  'de-DE': germanl,
  'it-IT': italianl,
  'pt-BR': brasilianl
};

const HVAAgentManagement = () => {
  const [agents, setAgents] = useState<GroupedAgents>({
    Development: [],
    POC: [],
    Staging: [],
    Production: []
  });
  const [userRole, setUserRole] = useState<string>('USER');
  const [allowedEnvironments, setAllowedEnvironments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['Development', 'POC', 'Staging', 'Production'])
  );

  // Filtros
  const [selectedGroup, setSelectedGroup] = useState<string>(() => {
    const savedFilter = localStorage.getItem('agentGroupFilter');
    return savedFilter || 'all';
  });
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);
  const [filteredAgents, setFilteredAgents] = useState<GroupedAgents>({
    Development: [],
    POC: [],
    Staging: [],
    Production: []
  });

  // Modal states
  const [showEnvironmentModal, setShowEnvironmentModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [targetEnvironment, setTargetEnvironment] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [agentToEdit, setAgentToEdit] = useState<Agent | null>(null);
  const [editFormData, setEditFormData] = useState({
    agentname: '',
    testtitle: '',
    clientowner: '',
    description: '',
    bxpname: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    agentname: '',
    testtitle: '',
    clientowner: 'System',
    language: 'es-ES',
    description: '',
    bxpname: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    null
  );
  const [availableCampaigns, setAvailableCampaigns] = useState<any[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [currentProductionAgent, setCurrentProductionAgent] = useState<
    any | null
  >(null);

  // Toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('info');

  const navigate = useNavigate();

  // Helper para obtener la bandera con fallback a español
  const getFlagImage = (flagName: string): string => {
    return languageToFlag[flagName as Language] || spanish;
  };

  // Cargar agentes al montar el componente
  useEffect(() => {
    fetchAgents();
  }, []);

  // Efecto para aplicar el filtro cuando cambia
  useEffect(() => {
    if (selectedGroup === 'all') {
      setFilteredAgents(agents);
    } else {
      setFilteredAgents({
        Development: agents.Development.filter(
          a => a.clientowner === selectedGroup
        ),
        POC: agents.POC.filter(a => a.clientowner === selectedGroup),
        Staging: agents.Staging.filter(a => a.clientowner === selectedGroup),
        Production: agents.Production.filter(
          a => a.clientowner === selectedGroup
        )
      });
    }
  }, [selectedGroup, agents]);

  // Guardar en localStorage cuando cambia
  useEffect(() => {
    localStorage.setItem('agentGroupFilter', selectedGroup);
  }, [selectedGroup]);

  // Cargar al iniciar
  useEffect(() => {
    const savedFilter = localStorage.getItem('agentGroupFilter');
    if (savedFilter && availableGroups.includes(savedFilter)) {
      setSelectedGroup(savedFilter);
    }
  }, [availableGroups]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await getAllAgentsByEnvironment();

      // Ordenar por nombre en cada environment
      const sortedAgents = {
        Development: response.agents.Development.sort((a, b) =>
          a.agentname.localeCompare(b.agentname)
        ),
        POC: response.agents.POC.sort((a, b) =>
          a.agentname.localeCompare(b.agentname)
        ),
        Staging: response.agents.Staging.sort((a, b) =>
          a.agentname.localeCompare(b.agentname)
        ),
        Production: response.agents.Production.sort((a, b) =>
          a.agentname.localeCompare(b.agentname)
        )
      };

      // Extraer todos los grupos únicos
      const allAgents = [
        ...sortedAgents.Development,
        ...sortedAgents.POC,
        ...sortedAgents.Staging,
        ...sortedAgents.Production
      ];

      const uniqueGroups = [
        ...new Set(allAgents.map(agent => agent.clientowner))
      ]
        .filter(group => group) // Eliminar valores vacíos
        .sort(); // Ordenar alfabéticamente

      setAvailableGroups(uniqueGroups);
      setAgents(sortedAgents);
      setFilteredAgents(sortedAgents); // Inicialmente mostrar todos
      setUserRole(response.role);
      setAllowedEnvironments(response.allowedEnvironments);
    } catch (error) {
      console.error('Error fetching agents:', error);
      showToastMessage('Error loading agents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      // Cargar las campañas outbound disponibles
      const campaigns = await fetchHVABXPCampaigns();

      // Filtrar solo campañas activas
      const activeCampaigns = campaigns.filter(c => c.active);

      setAvailableCampaigns(activeCampaigns);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      showToastMessage('Error loading campaigns', 'error');
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const checkCampaignCurrentAgent = async (campaignId: number) => {
    try {
      const result = await checkCampaignAgent(campaignId);

      if (result.hasAgent && result.agent) {
        setCurrentProductionAgent({
          agentid: result.agent.agentid,
          agentname: result.agent.agentname,
          environment: result.agent.environment
        });
      } else {
        setCurrentProductionAgent(null);
      }

      return result.agent;
    } catch (error) {
      console.error('Error checking campaign agent:', error);
      showToastMessage('Error checking campaign status', 'error');
      return null;
    }
  };

  const getImagePath = (bxpname: string): string | undefined => {
    return hvaImagesandVoices[bxpname]?.image;
  };

  const getEnvironmentIcon = (env: string) => {
    switch (env) {
      case 'Development':
        return faDigging;
      case 'POC':
        return faPersonDotsFromLine;
      case 'Staging':
        return faChargingStation;
      case 'Production':
        return faComments;
      default:
        return faCode;
    }
  };

  const languageToFlag: Partial<Record<Language, string>> = {
    spanish,
    english,
    american,
    greek,
    mexican,
    german,
    brasilian,
    italian,
    chilean,
    turkish,
    colombian,
    peruvian,
    argentine,
    french
    // Agregar más mapeos para nuevas banderas incorporadas
  };

  const handleTestAgent = (agent: Agent) => {
    // Si tiene campaña, usar su ID. Si no, -1 para modo test
    const campaignId = agent.campaign ? agent.campaign.bxpcampaignid : -1;

    navigate('/system/conversationalengine/agentfinetuning', {
      state: {
        agentId: agent.agentid,
        agentName: agent.agentname,
        campaignId: campaignId,
        agentImage: getImagePath(agent.bxpname),
        title: agent.testtitle
      }
    });
  };

  const handleDeleteAgent = (agent: Agent) => {
    setAgentToDelete(agent);
    setShowDeleteModal(true);
    setDeleteConfirmText(''); // Resetear el campo de confirmación
  };

  const handleEnvironmentChange = async (agent: Agent) => {
    setSelectedAgent(agent);
    setShowEnvironmentModal(true);
    setSelectedCampaignId(null);
    setCurrentProductionAgent(null);

    // Determinar opciones válidas según environment actual
    const transitions: Record<string, string[]> = {
      Development: ['POC', 'Staging'],
      POC: ['Development'],
      Staging: ['Development', 'Production'],
      Production: ['Staging']
    };

    const validTargets = transitions[agent.environment] || [];
    if (validTargets.length > 0) {
      setTargetEnvironment(validTargets[0]);

      // Si el destino puede ser Production, cargar campañas
      if (validTargets.includes('Production')) {
        await loadAvailableCampaigns();
      }
    }
  };

  const handleEditAgent = (agent: Agent) => {
    setAgentToEdit(agent);
    setEditFormData({
      agentname: agent.agentname,
      testtitle: agent.testtitle,
      clientowner: agent.clientowner,
      description: agent.description,
      bxpname: agent.bxpname
    });
    setShowEditModal(true);
  };

  const handleCreateAgent = async () => {
    // Validación básica
    if (!createFormData.agentname.trim()) {
      showToastMessage('Agent name is required', 'error');
      return;
    }

    if (!createFormData.description.trim()) {
      showToastMessage('Description is required', 'error');
      return;
    }

    setIsCreating(true);

    try {
      // Preparar datos con valores por defecto
      const agentData = {
        ...createFormData,
        testtitle:
          createFormData.testtitle || `Test - ${createFormData.agentname}`,
        environment: 'Development' as const,
        voice: createFormData.language.startsWith('es')
          ? 'es-ES-Standard-A'
          : 'en-US-Standard-A',
        firstmessage: createFormData.language.startsWith('es')
          ? 'Hola, soy {{agentName}} de {{companyName}}. ¿En qué puedo ayudarte?'
          : 'Hello, I am {{agentName}} from {{companyName}}. How can I help you?',
        inboundfirstmessage: createFormData.language.startsWith('es')
          ? 'Gracias por llamar. ¿En qué puedo ayudarte?'
          : 'Thank you for calling. How can I help you?',
        prompt: createFormData.language.startsWith('es')
          ? 'Eres un asistente virtual profesional y amable.'
          : 'You are a professional and friendly virtual assistant.',
        callresultprompt: 'Summarize the conversation.'
      };

      await createAgent(agentData);

      showToastMessage(
        `Agent "${createFormData.agentname}" created successfully`,
        'success'
      );
      setShowCreateModal(false);
      fetchAgents(); // Recargar la lista
    } catch (error) {
      showToastMessage('Error creating agent', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const confirmEnvironmentChange = async () => {
    if (!selectedAgent || !targetEnvironment) return;

    try {
      // Preparar los datos de la solicitud
      const requestData: any = {
        newEnvironment: targetEnvironment,
        reason: changeReason
      };

      // Si el destino es Production, incluir la campaña
      if (targetEnvironment === 'Production' && selectedCampaignId) {
        requestData.campaignId = selectedCampaignId;
      }

      const response = await changeAgentEnvironment(
        selectedAgent.agentid,
        requestData
      );

      // Mensaje de éxito personalizado
      let successMessage = `Agent moved from ${selectedAgent.environment} to ${targetEnvironment}`;

      // Si hubo reemplazo, añadir esa información
      if (response.replacedAgent) {
        successMessage += `. ${response.replacedAgent.message}`;
      }

      showToastMessage(successMessage, 'success');

      // Limpiar estados
      setShowEnvironmentModal(false);
      setSelectedAgent(null);
      setTargetEnvironment('');
      setChangeReason('');
      setSelectedCampaignId(null);
      setCurrentProductionAgent(null);

      // Recargar la lista de agentes
      fetchAgents();
    } catch (error) {
      showToastMessage('Error changing environment', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!agentToDelete) return;

    try {
      await deleteAgent(agentToDelete.agentid);
      showToastMessage(
        `Agent "${agentToDelete.agentname}" deleted successfully`,
        'success'
      );

      // Limpiar y cerrar modal
      setShowDeleteModal(false);
      setAgentToDelete(null);
      setDeleteConfirmText('');

      // Recargar lista
      fetchAgents();
    } catch (error) {
      showToastMessage('Error deleting agent', 'error');
    }
  };

  const confirmEditAgent = async () => {
    if (!agentToEdit) return;

    try {
      await updateAgent(agentToEdit.agentid, editFormData);

      showToastMessage(
        `Agent "${editFormData.agentname}" updated successfully`,
        'success'
      );

      setShowEditModal(false);
      setAgentToEdit(null);
      fetchAgents(); // Recargar lista
    } catch (error) {
      showToastMessage('Error updating agent', 'error');
    }
  };

  const toggleSection = (env: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(env)) {
      newExpanded.delete(env);
    } else {
      newExpanded.add(env);
    }
    setExpandedSections(newExpanded);
  };

  const showToastMessage = (
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const getValidTransitions = (env: string): string[] => {
    const transitions: Record<string, string[]> = {
      Development: ['POC', 'Staging'],
      POC: ['Development'],
      Staging: ['Development', 'Production'],
      Production: ['Staging']
    };
    return transitions[env] || [];
  };

  const canManageAgent = (env: string) => {
    if (userRole === 'SYSTEM_ADMIN') return true;
    if (['QA_ADMIN', 'OPS_ADMIN'].includes(userRole) && env === 'POC')
      return true;
    return false;
  };

  // Componente para seleccionar avatar del agente
  const AgentAvatarSelector = ({
    value,
    onChange
  }: {
    value: string;
    onChange: (bxpname: string) => void;
  }) => {
    // Obtener las opciones disponibles de hvaImagesandVoices
    const availableAvatars = Object.keys(hvaImagesandVoices).map(key => ({
      bxpname: key,
      image: hvaImagesandVoices[key]?.image,
      name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }));

    return (
      <div className="avatar-selector">
        <div className="p-2 pt-0 pb-0 pe-1 border rounded bg-body">
          <div className="w-100 p-0 m-0">
            <Form.Label className="fs-11 px-2">Agent Picture</Form.Label>
          </div>
          <div
            className="d-flex flex-wrap gap-2 p-1 pt-0 scrollbar"
            style={{ maxHeight: '130px', overflowY: 'auto' }}
          >
            {availableAvatars.map(avatar => (
              <div
                key={avatar.bxpname}
                className={`avatar-option p-0 border rounded-5 cursor-pointer ${
                  value === avatar.bxpname
                    ? 'border-primary bg-primary-subtle'
                    : 'border-secondary-subtle bg-body-emphasis'
                }`}
                onClick={() => onChange(avatar.bxpname)}
                style={{ cursor: 'pointer' }}
                title={avatar.name}
              >
                <Avatar
                  size="2xl"
                  src={avatar.image}
                  alt={avatar.name}
                  placeholder={!avatar.image}
                  variant="image"
                  rounded="circle"
                  imageClassName="avatar-image-top"
                />
              </div>
            ))}
          </div>
        </div>
        <Form.Text className="text-muted fs-10">
          Selected: {value || 'None'}
        </Form.Text>
      </div>
    );
  };

  return (
    <>
      <HVAToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      {/* Header y botón de nuevo agente */}
      <Row className="mb-4">
        <Col>
          <h2 className="mb-2 text-body-emphasis">Agent Management</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Manage Conversational Engine agents across environments
          </h5>
        </Col>
        <Col xs="auto" className="d-flex gap-2 align-items-end">
          {/* Selector de grupo */}
          <Form.Select
            size="sm"
            value={selectedGroup}
            onChange={e => setSelectedGroup(e.target.value)}
            style={{ minWidth: '150px' }}
            aria-label="Filter agents by group"
          >
            <option value="all">All Groups</option>
            {availableGroups.map(group => (
              <option key={group} value={group}>
                {group}
              </option>
            ))}
          </Form.Select>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setShowCreateModal(true);
              setCreateFormData({
                agentname: '',
                testtitle: '',
                clientowner: selectedGroup === 'all' ? 'System' : selectedGroup,
                language: 'es-ES',
                description: '',
                bxpname: ''
              });
            }}
            className="text-nowrap"
            disabled={userRole !== 'SYSTEM_ADMIN'}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            New Agent
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">Loading agents...</div>
      ) : (
        <div className="agents-container">
          {['Development', 'POC', 'Staging', 'Production'].map(env => {
            if (!allowedEnvironments.includes(env)) return null;

            const envAgents = filteredAgents[env as keyof GroupedAgents];
            const isExpanded = expandedSections.has(env);

            // Mostrar el conteo total y filtrado si hay filtro activo
            const totalCount = agents[env as keyof GroupedAgents].length;
            const filteredCount = envAgents.length;

            return (
              <Card key={env} className="mb-3">
                <Card.Header className="bg-secondary-subtle p-0">
                  <div
                    onClick={() => toggleSection(env)}
                    style={{ cursor: 'pointer', padding: '1rem' }}
                    className="d-block w-100"
                  >
                    <Row className="align-items-center">
                      <Col>
                        <h4 className="mb-0">
                          <FontAwesomeIcon
                            icon={getEnvironmentIcon(env)}
                            className="me-2"
                          />
                          {env}
                          <span className="fs-10 fw-normal ms-2">
                            {`(`}
                            {selectedGroup !== 'all' &&
                            totalCount !== filteredCount
                              ? `${filteredCount} of ${totalCount}`
                              : filteredCount}{' '}
                            agents
                            {selectedGroup !== 'all' && ` in ${selectedGroup}`}
                            {`)`}
                          </span>
                        </h4>
                        <p className="mb-0 text-muted fs-9 px-5">
                          {env === 'Production' && 'Live agents with campaigns'}
                          {env === 'Staging' && 'Ready for production'}
                          {env === 'POC' && 'Proof of concepts'}
                          {env === 'Development' && 'Work in progress'}
                        </p>
                      </Col>
                      <Col xs="auto">
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronUp : faChevronDown}
                          className="text-primary fs-9"
                        />
                      </Col>
                    </Row>
                  </div>
                </Card.Header>
                {isExpanded && (
                  <Card.Body className="pb-2 px-5">
                    <Row>
                      {envAgents.length === 0 ? (
                        <Col>
                          <div className="text-center text-muted py-4 fs-9">
                            No agents in {env}
                          </div>
                        </Col>
                      ) : (
                        envAgents.map(agent => (
                          <Col
                            key={agent.agentid}
                            lg={12}
                            xl={6}
                            xxl={4}
                            className="mb-3 px-2"
                          >
                            {/* Tarjeta del agente */}
                            <Card className="h-100 shadow-sm">
                              <Card.Body className="bg-body rounded-3 pt-2 pb-2">
                                <div className="d-flex">
                                  <Avatar
                                    size="3xl"
                                    src={getImagePath(agent.bxpname)}
                                    alt={agent.agentname}
                                    placeholder={!getImagePath(agent.bxpname)}
                                    variant="image"
                                    rounded="circle"
                                    imageClassName="avatar-image-top border bg-body-emphasis"
                                    className="me-3"
                                  />
                                  {/* Nombre y detalles */}
                                  <div className="flex-grow-1 pt-2 pb-0">
                                    <div>
                                      <h5 className="mb-1">
                                        {agent.agentname}
                                      </h5>
                                    </div>
                                    <div className="small text-muted mb-2">
                                      <div className="fw-bold lh-2">
                                        {agent.testtitle}
                                      </div>
                                      <div className="lh-1 fs-10">
                                        Group: {agent.clientowner}
                                      </div>
                                      {agent.campaign && (
                                        <div className="lh-1 fs-10">
                                          Campaign:{' ('}
                                          {agent.campaign?.bxpcampaignid}
                                          {') '}
                                          {agent.campaign?.name}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {/* Bandera de idioma */}
                                  <img
                                    src={getFlagImage(agent.flag)}
                                    alt="Language"
                                    className="position-absolute"
                                    style={{ top: '0px', right: '0px' }}
                                  />
                                </div>
                                {/* Descripción */}
                                <div className="mt-2">
                                  <div
                                    className="text-body fs-9 mb-2 lh-sm scrollbar"
                                    style={{
                                      maxHeight: '60px',
                                      minHeight: '60px',
                                      overflowY: 'auto'
                                    }}
                                  >
                                    {agent.description}
                                  </div>
                                  {/* Botones de acción */}
                                  <div className="d-flex gap-1 flex-wrap justify-content-end">
                                    {canManageAgent(env) && (
                                      <>
                                        <Button
                                          variant="phoenix-secondary"
                                          size="sm"
                                          onClick={() => handleEditAgent(agent)}
                                          title="Edit agent"
                                          className="p-1 lh-1 border-0 text-body-quaternary"
                                        >
                                          <FontAwesomeIcon icon={faEdit} />
                                        </Button>

                                        {getValidTransitions(env).length >
                                          0 && (
                                          <Button
                                            variant="phoenix-secondary"
                                            size="sm"
                                            onClick={() =>
                                              handleEnvironmentChange(agent)
                                            }
                                            title="Change environment"
                                            className="p-1 lh-1 border-0 text-body-quaternary"
                                          >
                                            <FontAwesomeIcon
                                              icon={faExchange}
                                            />
                                          </Button>
                                        )}

                                        {env !== 'Production' && (
                                          <>
                                            {['Development'].includes(env) && (
                                              <Button
                                                variant="phoenix-secondary"
                                                size="sm"
                                                onClick={() =>
                                                  handleDeleteAgent(agent)
                                                }
                                                title="Delete agent"
                                                className="p-1 lh-1 border-0 text-body-quaternary"
                                              >
                                                <FontAwesomeIcon
                                                  icon={faTrash}
                                                />
                                              </Button>
                                            )}
                                          </>
                                        )}

                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={() => handleTestAgent(agent)}
                                          className="p-0 rounded-pill mx-2 me-0 text-nowrap"
                                        >
                                          <div className="p-0 px-3 lh-1">
                                            Try & Refine
                                            <FontAwesomeIcon
                                              icon={faPlay}
                                              className="mx-2 me-0"
                                            />
                                          </div>
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))
                      )}
                    </Row>
                  </Card.Body>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal para cambio de environment */}
      <Modal
        show={showEnvironmentModal}
        onHide={() => {
          setShowEnvironmentModal(false);
          setSelectedCampaignId(null);
          setCurrentProductionAgent(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Agent Environment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 pt-4">
          {selectedAgent && (
            <>
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3">
                  <Avatar
                    size="2xl"
                    src={getImagePath(selectedAgent.bxpname)}
                    alt={selectedAgent.agentname}
                    placeholder={!getImagePath(selectedAgent.bxpname)}
                    variant="image"
                    rounded="circle"
                    imageClassName="avatar-image-top border"
                    className="me-3"
                  />
                  <div>
                    <h5 className="mb-1">{selectedAgent.agentname}</h5>
                    <div className="text-muted small">
                      Current environment:
                      <Badge className="ms-2 bg-warning-subtle text-dark">
                        {selectedAgent.environment}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <Form>
                <Form.Floating className="mb-3">
                  <Form.Select
                    value={targetEnvironment}
                    onChange={async e => {
                      const newEnv = e.target.value;
                      setTargetEnvironment(newEnv);
                      setSelectedCampaignId(null);
                      setCurrentProductionAgent(null);

                      // Si el nuevo environment es Production, cargar campañas
                      if (newEnv === 'Production') {
                        await loadAvailableCampaigns();
                      }
                    }}
                    className="bg-body"
                    aria-label="Target Environment"
                  >
                    {getValidTransitions(selectedAgent.environment).map(env => (
                      <option key={env} value={env}>
                        {env}
                      </option>
                    ))}
                  </Form.Select>
                  <label>Target Environment</label>
                </Form.Floating>

                {/* Selector de campaña cuando el destino es Production */}
                {targetEnvironment === 'Production' && (
                  <>
                    <Form.Floating className="mb-3">
                      <Form.Select
                        value={selectedCampaignId || ''}
                        onChange={async e => {
                          const campaignId = e.target.value
                            ? parseInt(e.target.value)
                            : null;
                          setSelectedCampaignId(campaignId);

                          // Verificar si la campaña ya tiene un agente
                          if (campaignId) {
                            await checkCampaignCurrentAgent(campaignId);
                          } else {
                            setCurrentProductionAgent(null);
                          }
                        }}
                        className="bg-body"
                        disabled={loadingCampaigns}
                        required
                        aria-label="Campaign Selection"
                      >
                        <option value="">Select a campaign...</option>
                        {availableCampaigns.map(campaign => (
                          <option key={campaign.bxpid} value={campaign.bxpid}>
                            ({campaign.bxpid}) {campaign.name} -{' '}
                            {campaign.servicename}
                          </option>
                        ))}
                      </Form.Select>
                      <label>Production Campaign *</label>
                    </Form.Floating>

                    {loadingCampaigns && (
                      <div className="text-center py-2">
                        <FontAwesomeIcon
                          icon={faCircleNotch}
                          spin
                          className="me-2"
                        />
                        Loading campaigns...
                      </div>
                    )}
                  </>
                )}
                <Form.Floating>
                  <Form.Control
                    as="textarea"
                    style={{ minHeight: '100px' }}
                    value={changeReason}
                    onChange={e => setChangeReason(e.target.value)}
                    placeholder="Reason for change"
                    className="bg-body shadow-none"
                  />
                  <label>Reason for Change (Optional)</label>
                </Form.Floating>
              </Form>

              {/* Advertencia cuando hay un agente que será reemplazado */}
              {currentProductionAgent && targetEnvironment === 'Production' && (
                <div className="bg-danger-subtle mt-3 mb-0 p-3 rounded fs-9">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="me-2 mt-1"
                  />
                  <strong>Warning: Agent Replacement</strong>
                  <br />
                  The campaign currently has the agent{' '}
                  <strong>{currentProductionAgent.agentname}</strong> in
                  Production.
                  <br />
                  If you proceed, this agent will be automatically moved to{' '}
                  <strong>Staging</strong> and replaced by{' '}
                  <strong>{selectedAgent.agentname}</strong>.
                </div>
              )}

              {targetEnvironment === 'Production' && !selectedCampaignId && (
                <div className="bg-warning-subtle mt-3 mb-0 p-3 rounded fs-9">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="me-2"
                  />
                  <strong>Important</strong>
                  <br /> You must select a campaign to move the agent to
                  Production.
                </div>
              )}

              {targetEnvironment === 'Staging' &&
                selectedAgent.environment === 'Production' && (
                  <div className="bg-warning-subtle mt-3 mb-0 p-3 rounded fs-9">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="me-2"
                    />
                    <strong>Note</strong>
                    <br /> You are rolling back from Production to Staging. The
                    agent will no longer be live and will be unassigned from its
                    campaign.
                  </div>
                )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEnvironmentModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmEnvironmentChange}
            disabled={
              !targetEnvironment ||
              (targetEnvironment === 'Production' && !selectedCampaignId)
            }
          >
            <FontAwesomeIcon icon={faExchange} className="me-2" />
            Change Environment
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal para eliminar agente */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Agent</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 pt-4">
          {agentToDelete && (
            <>
              <div className="bg-warning-subtle p-3 rounded mb-3 fs-9">
                <strong>Warning!</strong>
                <br /> You are about to permanently delete the agent
                <br /> This action cannot be undone.
              </div>

              <div className="bg-body p-3 rounded mb-3">
                <h5>{agentToDelete.agentname}</h5>
                <p className="mb-1 text-muted fs-9">
                  Environment:{' '}
                  <Badge bg="secondary">{agentToDelete.environment}</Badge>
                </p>
                <p className="mb-0 text-muted small">
                  ID: {agentToDelete.agentid}
                </p>
              </div>

              <Form.Group>
                <Form.Label className="text-danger">
                  Type <strong>{agentToDelete.agentname}</strong> to confirm
                  deletion:
                </Form.Label>
                <Form.Control
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="Enter agent name to confirm"
                  className="bg-body"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={
              deleteConfirmText.toLowerCase().trim() !==
              agentToDelete?.agentname.toLowerCase().trim()
            }
          >
            Delete Agent
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal para editar datos básicos del agente */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Agent Information</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 pb-4">
          {agentToEdit && (
            <Form>
              <div className="d-flex gap-2 mb-3">
                <Form.Floating className="flex-grow-1">
                  <Form.Control
                    type="text"
                    value={editFormData.agentname}
                    onChange={e =>
                      setEditFormData({
                        ...editFormData,
                        agentname: e.target.value
                      })
                    }
                    placeholder="Agent name"
                    className="bg-body"
                  />
                  <label>Agent Name</label>
                </Form.Floating>

                <div
                  className="position-relative"
                  style={{ minWidth: '200px' }}
                >
                  {/* Preview del avatar actual */}
                  <div
                    className="position-absolute"
                    style={{
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      pointerEvents: 'none'
                    }}
                  >
                    <Avatar
                      size="l"
                      src={getImagePath(editFormData.bxpname)}
                      alt="Avatar"
                      placeholder={!getImagePath(editFormData.bxpname)}
                      variant="image"
                      rounded="circle"
                      imageClassName="avatar-image-top border"
                    />
                  </div>

                  <Form.Floating>
                    <Form.Select
                      value={editFormData.bxpname}
                      onChange={e =>
                        setEditFormData({
                          ...editFormData,
                          bxpname: e.target.value
                        })
                      }
                      className="bg-body"
                      style={{ paddingLeft: '60px' }}
                      aria-label="Agent Picture Selection"
                    >
                      <option value="">No picture</option>
                      {Object.keys(hvaImagesandVoices).map(key => (
                        <option key={key} value={key}>
                          {key}
                        </option>
                      ))}
                    </Form.Select>
                    <label style={{ paddingLeft: '65px' }}>Agent Picture</label>
                  </Form.Floating>
                </div>
              </div>

              <Form.Floating className="mb-3">
                <Form.Control
                  type="text"
                  value={editFormData.testtitle}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      testtitle: e.target.value
                    })
                  }
                  placeholder="Service short description"
                  className="bg-body"
                />
                <label>Service short description</label>
              </Form.Floating>

              <Form.Floating className="mb-3">
                <Form.Control
                  type="text"
                  value={editFormData.clientowner}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      clientowner: e.target.value
                    })
                  }
                  placeholder="Group"
                  className="bg-body"
                />
                <label>Group</label>
              </Form.Floating>

              <Form.Floating className="mb-2">
                <Form.Control
                  as="textarea"
                  style={{ minHeight: '100px' }}
                  value={editFormData.description}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      description: e.target.value.slice(0, 500)
                    })
                  }
                  placeholder="Description"
                  className="bg-body shadow-none"
                  maxLength={500}
                />
                <label>Description</label>
              </Form.Floating>
              <Form.Text className="text-muted">
                {editFormData.description.length}/500 characters
              </Form.Text>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmEditAgent}
            disabled={!editFormData.agentname.trim()}
          >
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal para crear nuevo agente */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Agent</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6">
          <Form>
            {/* Nombre del agente */}
            <Form.Floating className="mb-3">
              <Form.Control
                type="text"
                value={createFormData.agentname}
                onChange={e =>
                  setCreateFormData({
                    ...createFormData,
                    agentname: e.target.value
                  })
                }
                placeholder="Agent name"
                className="bg-body"
                required
              />
              <label>Agent Name *</label>
            </Form.Floating>

            {/* Selector de Avatar */}
            <div className="mb-1">
              <AgentAvatarSelector
                value={createFormData.bxpname}
                onChange={bxpname =>
                  setCreateFormData({
                    ...createFormData,
                    bxpname
                  })
                }
              />
            </div>

            {/* Test title */}
            <Form.Floating className="mb-3">
              <Form.Control
                type="text"
                value={createFormData.testtitle}
                onChange={e =>
                  setCreateFormData({
                    ...createFormData,
                    testtitle: e.target.value
                  })
                }
                placeholder={`Test - ${
                  createFormData.agentname || 'Agent Name'
                }`}
                className="bg-body"
              />
              <label>Service short description</label>
            </Form.Floating>

            {/* Fila con Owner y Language */}
            <div className="d-flex gap-2 mb-3">
              {/* Owner/Group */}
              <Form.Floating className="flex-grow-1">
                <Form.Control
                  type="text"
                  value={createFormData.clientowner}
                  onChange={e =>
                    setCreateFormData({
                      ...createFormData,
                      clientowner: e.target.value
                    })
                  }
                  placeholder="Group"
                  className="bg-body"
                />
                <label>Group</label>
              </Form.Floating>

              {/* Language con bandera */}
              <div className="flex-grow-1 position-relative">
                <img
                  src={LANGUAGE_FLAGS[createFormData.language] || spanish}
                  alt="Language flag"
                  style={{
                    width: 'auto',
                    height: '50px',
                    position: 'absolute',
                    left: '1px',
                    top: '1px',
                    zIndex: 10,
                    pointerEvents: 'none'
                  }}
                />
                <Form.Floating>
                  <Form.Select
                    value={createFormData.language}
                    onChange={e =>
                      setCreateFormData({
                        ...createFormData,
                        language: e.target.value
                      })
                    }
                    className="bg-body"
                    style={{ paddingLeft: '35px' }}
                    aria-label="Language Selection"
                  >
                    <option value="es-ES">Spanish (Spain)</option>
                    <option value="es-MX">Spanish (Mexico)</option>
                    <option value="es-CL">Spanish (Chile)</option>
                    <option value="en-US">English (US)</option>
                    <option value="en-GB">English (UK)</option>
                    <option value="pt-BR">Portuguese (Brazil)</option>
                    <option value="fr-FR">French (France)</option>
                    <option value="de-DE">German (Germany)</option>
                    <option value="it-IT">Italian (Italy)</option>
                    <option value="el-GR">Greek (Greece)</option>
                  </Form.Select>
                  <label style={{ paddingLeft: '40px' }}>Language</label>
                </Form.Floating>
              </div>
            </div>

            {/* Description */}
            <Form.Floating>
              <Form.Control
                as="textarea"
                style={{ minHeight: '100px' }}
                value={createFormData.description}
                onChange={e =>
                  setCreateFormData({
                    ...createFormData,
                    description: e.target.value.slice(0, 500)
                  })
                }
                placeholder="Description"
                className="bg-body shadow-none"
                maxLength={500}
              />
              <label>Description *</label>
            </Form.Floating>
            <Form.Text className="text-muted">
              {createFormData.description.length}/500 characters
            </Form.Text>
          </Form>

          <div className="bg-warning-subtle rounded p-3 mt-3 mb-0">
            <div className="d-flex align-items-start">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1" />
              <small>
                The agent will be created in <strong>Development</strong>{' '}
                environment. Advanced configuration (prompts, tools, adapters)
                can be customized in agent fine-tuning.
              </small>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateModal(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateAgent}
            disabled={
              isCreating ||
              !createFormData.agentname.trim() ||
              !createFormData.description.trim()
            }
          >
            {isCreating ? (
              <>
                <FontAwesomeIcon icon={faCircleNotch} spin className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create Agent
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default HVAAgentManagement;
